import React from 'react';
import { NavigateOptions } from '@reach/router';
import { navigate } from 'gatsby';
import DynamicRoutingContext from './DynamicRoutingContext';

interface Props {
  basePath?: string;
  parameters?: Record<string, any>;
  children: React.ReactNode;
}

/**
 * This provider exposes the routing parameters and a function to allow to
 *    navigate in the application.
 *
 * @param {string} basePath The base path of your application. If defined, it will
 *    be used to calculate the address when navigating, otherwise, the path passed
 *    to the navigateTo function will be used alone.
 *
 * @param { object } parameters The routing parameters that will be provided to the
 *    context consumers.
 */
const RoutingProvider = ({ basePath, parameters, children }: Props) => {
  /**
   * Allows navigating inside the application, using the basePath and the provided
   *    path.
   *
   * @param {string} path The relative path to navigate to. The basePath value will
   *    be concatenated to this value.
   * (e.g. 'mine/BDVDVG400163243B/block')
   * @param {object} options An object to store on location state. This is useful
   *    for state that doesn’t need to be in the URL but is associated with a
   *    route transition.
   */
  function navigateTo(path: string, options?: NavigateOptions<any>) {
    navigate(
      `${normalizePathPart(basePath)}${normalizePathPart(path)}`,
      options
    );
  }

  return (
    <DynamicRoutingContext.Provider value={{ ...parameters, navigateTo }}>
      {children}
    </DynamicRoutingContext.Provider>
  );
};

function normalizePathPart(pathPart: string): string {
  if (!pathPart) return '';

  let normalizedValue = String(pathPart);

  if (!normalizedValue.startsWith('/')) {
    // adds a '/' at the beginning of the string
    normalizedValue = `/${normalizedValue}`;
  }

  if (normalizedValue.endsWith('/')) {
    // removes the '/' at the end of the string}
    normalizedValue = normalizedValue.slice(0, -1);
  }

  return normalizedValue;
}

export default RoutingProvider;
